import { useCallback, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import { useStore } from '@/store'
import { clampTableH } from '@/lib/layout/splitMath'

type Handlers = {
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void
  onPointerUp:   () => void
}

// Drag-to-resize behaviour for the viz/table divider. The divider's vertical
// position controls the table height; dragging up grows the table.
export function useResizableTable(hostRef: RefObject<HTMLElement | null>): Handlers {
  const tableH = useStore(s => s.tableH)
  const setTableH = useStore(s => s.setTableH)
  const dragRef = useRef<{ startY: number; startH: number } | null>(null)

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = { startY: e.clientY, startH: tableH }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }, [tableH])

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    const dy = dragRef.current.startY - e.clientY
    const hostH = hostRef.current?.getBoundingClientRect().height ?? 0
    setTableH(clampTableH(dragRef.current.startH + dy, hostH))
  }, [hostRef, setTableH])

  const onPointerUp = useCallback(() => {
    dragRef.current = null
  }, [])

  return { onPointerDown, onPointerMove, onPointerUp }
}